import React, { useState, useMemo } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useBusinessRolesWithPermissions } from "../../hooks/useBusinessRolesWithPermissions";
import { BusinessRoleWithPermissions } from "../../api/fetchBusinessRolesWithPermissions/types";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";

const BusinessRolesList: React.FC = () => {
  const { roles, loading, error } = useBusinessRolesWithPermissions();
  const [expandedRoles, setExpandedRoles] = useState<Set<number>>(new Set());

  const toggleRole = (roleId: number) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedRoles(new Set(roles.map((r) => r.id)));
  };

  const collapseAll = () => {
    setExpandedRoles(new Set());
  };

  if (loading) {
    return (
      <MainPageWrapper title="Business Roles & Permissions">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </MainPageWrapper>
    );
  }

  if (error) {
    return (
      <MainPageWrapper title="Business Roles & Permissions">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
          {error}
        </div>
      </MainPageWrapper>
    );
  }

  return (
    <MainPageWrapper
      title="Business Roles & Permissions"
      actions={
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            Collapse All
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {roles.map((role) => (
          <RoleCard
            key={role.id}
            role={role}
            isExpanded={expandedRoles.has(role.id)}
            onToggle={() => toggleRole(role.id)}
          />
        ))}
      </div>
    </MainPageWrapper>
  );
};

interface RoleCardProps {
  role: BusinessRoleWithPermissions;
  isExpanded: boolean;
  onToggle: () => void;
}

const RoleCard: React.FC<RoleCardProps> = ({ role, isExpanded, onToggle }) => {
  const sortedPermissions = useMemo(() => {
    return [...role.permissions].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [role.permissions]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "p-2 rounded-lg",
              role.isCertainPathOnly ? "bg-purple-100" : "bg-blue-100",
            )}
          >
            <UserGroupIcon
              className={cn(
                "h-5 w-5",
                role.isCertainPathOnly ? "text-purple-600" : "text-blue-600",
              )}
            />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{role.label}</h3>
              {role.isCertainPathOnly && (
                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                  CertainPath Only
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{role.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {role.permissionCount} permission
            {role.permissionCount !== 1 ? "s" : ""}
          </span>
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {role.description && (
            <p className="text-sm text-gray-600 mt-3 mb-4">{role.description}</p>
          )}

          {sortedPermissions.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-2">
              No permissions assigned
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
              {sortedPermissions.map((permission) => (
                <div
                  key={permission.id}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-md text-sm",
                    permission.isCertainPathOnly
                      ? "bg-purple-50 text-purple-800"
                      : "bg-gray-50 text-gray-700",
                  )}
                >
                  <ShieldCheckIcon
                    className={cn(
                      "h-4 w-4 flex-shrink-0",
                      permission.isCertainPathOnly
                        ? "text-purple-500"
                        : "text-gray-400",
                    )}
                  />
                  <span className="truncate" title={permission.label}>
                    {permission.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessRolesList;
